import { useLocation } from 'react-router-dom';
import { ChapterTabList, ChapterTools } from '../components/learn/ChapterTabs';
import { StudySidebar } from '../components/learn/StudySidebar';
import { useChapterList } from '../hooks/useChapterList';
import { usePageTitle } from '../hooks/usePageTitle';
import '../styles/study.css';

/**
 * The Study landing page (feature 023, FR-005a).
 *
 * Feature 023 layout revamp: the page adopts the design system's `.studybody` — the chapter tab
 * rail standing beside a reading page. The rail is the same component the chapter reader shows, so
 * the whole book is one click away from either surface.
 *
 * On phones (and in the landscape-phone band) `study.css` hides the rail, and the same tabs and
 * tools are laid on the page as a single sheet instead — see the `study-phone-only` block below.
 */
export function LearnPage() {
  usePageTitle('Study — Hawkes Bridge');
  const location = useLocation();
  const { chapters, failed } = useChapterList();
  const originPath = `${location.pathname}${location.search}`;

  return (
    <div className="study-body study-body--page w-full">
      <StudySidebar chapters={chapters} failed={failed} originPath={originPath} />

      <div className="w-full max-w-[820px] px-4 py-8 md:px-10 md:py-[30px]">
        <h1 className="font-serif text-2xl text-ink">Study</h1>
        <div className="mt-2.5 mb-6 h-0.5 w-16 bg-brass" aria-hidden="true" />

        <section aria-labelledby="precision-notes-heading">
          <h2 id="precision-notes-heading" className="font-serif text-xl text-ink">
            Precision notes
          </h2>
          <p className="mt-2 mb-8 max-w-[640px] text-[15px] leading-[1.7] text-ink-soft">
            The complete Precision system, chapter by chapter — the same notes Jeff teaches from.
            Pick a chapter to start reading.
          </p>

          {/*
            The rail, laid on the page. Where there is no room for a 250px column the book does not
            fall back to a different design — it lies flat as a single cream sheet under a felt
            band, the same surface the reader's header and the notes' table heads use, carrying the
            SAME components as the rail so the two can never drift apart.

            `study-phone-only` hides this the moment the rail appears (study.css), so exactly one of
            the two is ever rendered. The landmark takes its name from its own visible heading
            rather than repeating the rail's "Chapters" — both are in the DOM at once, and two
            same-named navigation landmarks is a duplicate a stylesheet should not be relied on to
            resolve.
          */}
          <div className="study-phone-only mt-6 overflow-hidden rounded-xl border border-line bg-cream shadow-sm">
            <h3
              id="study-sheet-heading"
              className="border-b-[3px] border-brass bg-felt px-[22px] py-2.5 font-serif text-base font-bold text-cream"
            >
              The notes
            </h3>

            <nav aria-labelledby="study-sheet-heading" className="py-2">
              <ChapterTabList chapters={chapters} failed={failed} originPath={originPath} />
            </nav>

            <div className="border-t border-line py-2">
              <h3 className="px-[22px] pb-1.5 text-xs font-semibold text-ink-soft">All chapters</h3>
              <ChapterTools />
            </div>
          </div>
        </section>

        <section aria-labelledby="more-study-heading" className="mt-10">
          <h2 id="more-study-heading" className="font-serif text-xl text-ink">
            More to come
          </h2>
          <ul className="mt-3 max-w-[640px] space-y-2">
            {['Counting', 'Opening Lead'].map((label) => (
              <li
                key={label}
                className="flex items-center justify-between rounded-lg border border-line bg-card px-4 py-3"
              >
                <span className="font-medium text-ink">{label}</span>
                <span className="rounded-full bg-[#eee5cd] px-2.5 py-1 text-xs font-extrabold tracking-wider text-[#6b6350]">
                  COMING SOON
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
